import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { v4 as uuidv4 } from 'uuid';
import { getByPartitionKey } from '../../lib/services/api-service';
import { TableName } from '../../constants/table';
import TenantContext from '../../lib/context/TenantContext';
import { useGetStockMaster } from '../../lib/query/stockMaster';
import { addEntity } from '../../lib/services/api-service';
import { FormInputText } from '../formComponent/FormInputText';

export interface IStockMaster {
  stockDebitLedger: string;
  unitOfMeasurement: string;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  gstRate: number;
  companyId: string;
  vendorId: string;
  taxCategory: number;
  hsnsac?: string;
}

interface ICompany {
  RowKey: string;
  CompanyName: string;
  Active: boolean;
}

interface ICommonCode {
  RowKey: string;
  CommonCode: string;
  Type: string;
  CompanyId?: string;
}

const AddStockMaster = (props: {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { tenant } = React.useContext(TenantContext);
  const { refetch } = useGetStockMaster(tenant.id);
  const [companies, setCompanies] = React.useState<ICompany[]>([]);
  const [vendors, setVendors] = React.useState<ICommonCode[]>([]);

  React.useEffect(() => {
    const fetchData = async () => {
      const companyData = await getByPartitionKey(
        TableName.CompanyMaster,
        tenant.id,
      );
      const commonCodeData = await getByPartitionKey(
        TableName.CommonCode,
        tenant.id,
      );

      const uniqueCompanies = Array.from(
        new Map(
          companyData
            .filter((item: ICompany) => item.Active === true)
            .map((item: ICompany) => [item.CompanyName, item]),
        ).values(),
      ) as ICompany[];
      setCompanies(uniqueCompanies);

      const uniqueVendors = Array.from(
        new Map(
          commonCodeData.map((item: ICommonCode) => [item.CommonCode, item]),
        ).values(),
      ) as ICommonCode[];
      setVendors(uniqueVendors);
    };
    fetchData();
  }, []);

  const schema = yup
    .object()
    .shape({
      stockDebitLedger: yup.string().required('Stock Debit Ledger is required'),
      unitOfMeasurement: yup
        .string()
        .required('Unit of Measurement is required'),
      cgstRate: yup.number().required('CGST Rate is required'),
      sgstRate: yup.number().required('SGST Rate is required'),
      igstRate: yup.number().required('IGST Rate is required'),
      gstRate :yup.number().required('GST Rate is required'),
      companyId: yup.string().required('Company is required'),
      vendorId: yup.string().required('Vendor is required'),
      taxCategory: yup.number().required('Tax Category is required'),
      hsnsac: yup.string(),
    })
    .required();

  const { handleSubmit, control, reset, watch } = useForm<IStockMaster>({
    resolver: yupResolver(schema),
  });

  const companyId = watch('companyId');

  const onSubmit = async (data: IStockMaster) => {
    const existingStockMasters = await getByPartitionKey(
      TableName.StockMaster,
      tenant.id.toLowerCase(),
    );

    const isDuplicate = existingStockMasters.some(
      (item: any) =>
        item.CompanyId === data.companyId &&
        item.VendorId === data.vendorId &&
        item.StockOCR === data.stockDebitLedger,
    );

    if (isDuplicate) {
      toast.warn('Stock already exists.');
      return;
    }

    const uuid = uuidv4();
    await addEntity(TableName.StockMaster, {
      partitionKey: tenant.id.toLowerCase(),
      rowKey: uuid,
      StockDebitLedger: data.stockDebitLedger,
      UnitOfMeasurement: data.unitOfMeasurement,
      CGSTRate: data.cgstRate,
      SGSTRate: data.sgstRate,
      IGSTRate: data.igstRate,
      GSTRate: data.gstRate,
      CompanyId: data.companyId,
      VendorId: data.vendorId,
      StockOCR: data.stockDebitLedger,
      TaxCategory: data.taxCategory,
      HSNSAC: data.hsnsac,
      IsSystemGenerated: false,
    })
      .then(() => {
        toast.success(
          `Successfully added new Stock with name: ${data.stockDebitLedger}`,
        );
        refetch();
        props.setShowDialog(false);
        reset();
      })
      .catch(() => {
        toast.error('something went wrong');
        props.setShowDialog(false);
      });
  };

  const handleClose = () => {
    props.setShowDialog(false);
    reset();
  };

  return (
    <React.Fragment>
      <Dialog open={props.showDialog} onClose={handleClose}>
        <DialogTitle>Add Stock Master</DialogTitle>

        <DialogContent sx={{ width: '400px' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-4">
              <FormControl fullWidth>
                <InputLabel id="company-select-label">Company Name</InputLabel>
                <Controller
                  name="companyId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="company-select-label"
                      label="Company Name">
                      {companies
                        .sort((a, b) => {
                          return (a.CompanyName ?? '').localeCompare(
                            b.CompanyName ?? '',
                          );
                        })
                        .map(company => (
                          <MenuItem key={company.RowKey} value={company.RowKey}>
                            {company.CompanyName}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                />
              </FormControl>
            </div>
            <div className="mt-4">
              <FormControl fullWidth>
                <InputLabel id="vendor-select-label">Vendor Name</InputLabel>
                <Controller
                  name="vendorId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      disabled={!companyId}
                      labelId="vendor-select-label"
                      label="Vendor Name">
                      {vendors
                        .filter(vendor => vendor.CompanyId === companyId)
                        .sort((a, b) => {
                          return (a.CommonCode ?? '').localeCompare(
                            b.CommonCode ?? '',
                          );
                        })
                        .map(vendor => (
                          <MenuItem key={vendor.RowKey} value={vendor.RowKey}>
                            {vendor.CommonCode}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                />
              </FormControl>
            </div>
            <div className="mt-4">
              <FormInputText
                name="stockDebitLedger"
                control={control}
                label="Stock Debit Ledger"
              />
            </div>
            <div className="mt-4">
              <FormInputText
                name="unitOfMeasurement"
                control={control}
                label="Unit of Measurement"
              />
            </div>
            <div className="mt-4">
              <FormInputText
                name="cgstRate"
                control={control}
                label="CGST Rate"
              />
            </div>
            <div className="mt-4">
              <FormInputText
                name="sgstRate"
                control={control}
                label="SGST Rate"
              />
            </div>
            <div className="mt-4">
              <FormInputText
                name="igstRate"
                control={control}
                label="IGST Rate"
              />
            </div>
             <div className="mt-4">
              <FormInputText
                name="gstRate"
                control={control}
                label="GST Rate"
              />
            </div>
            <div className="mt-4">
              <FormInputText
                name="taxCategory"
                control={control}
                label="Tax Category"
              />
            </div>
            <div className="mt-4">
              <FormInputText name="hsnsac" control={control} label="HSN/SAC" />
            </div>
            <div className="mt-6 flex items-end justify-end space-x-4">
              <Button onClick={handleClose} variant="contained">
                Discard
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Add
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default AddStockMaster;
